import { useMemo, useState } from 'react'
import { Button } from '@heroui/react'

import { ModuleIntro } from '@/components/layout'
import { errorMessage } from '@/lib/api/api-error'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { DownloadIcon, PlusIcon, UploadIcon } from '@/components/ui/icons'

import {
  useProductActions,
  useProductBrands,
  useProductCategories,
  useProductImport,
  useProducts,
} from '../hooks'
import { emptyProductFilters } from '../types'
import type { ProductFilterValues, ProductListQuery, ProductSummary } from '../types'
import { DeleteProductDialog } from './delete-product-dialog'
import { ImportProductsDialog } from './import-products-dialog'
import { ProductFilters } from './product-filters'
import { ProductFormDialog } from './product-form-dialog'
import { ProductTable } from './product-table'
import type { ProductRowAction } from './product-table'

/** The API's own default page, and enough rows to fill the table without scrolling. */
const PAGE_SIZE = 10

interface ProductsPanelProps {
  /**
   * The outlet chosen in the module bar, or null for the whole business.
   *
   * Owned by the page rather than by this panel: the bar that holds the picker
   * is the page's, and a panel that owned the value would have to push it
   * upwards to get it drawn.
   */
  branchId?: string | null
  /**
   * Clears that choice from here.
   *
   * The panel owns "clear the filters" because it owns the empty state that
   * offers it, but the outlet is not its value to reset — so the page lends it
   * a way to. Without this, clearing filters on an empty branch would visibly
   * do nothing.
   */
  onClearBranch?: () => void
  /**
   * A brand to open filtered by — how the Brands tab's "Show products" lands
   * here. Read once, on mount: the panel remounts each time its tab is
   * selected, and after that the filter is the merchant's to change.
   */
  initialBrandId?: string | null
}

/**
 * The catalogue itself — one tab of the Products module.
 *
 * A panel rather than a page since Categories and Modifiers were folded in
 * beside it: the four tabs are one screen, and a screen's worth of state per
 * tab does not belong in the file that draws the tab bar. Same arrangement
 * `EmployeesPanel` introduced.
 *
 * Its filters sit in the panel body rather than the module bar, unlike the
 * other three tabs. There are four of them and one is a text search — a row
 * that wide beside the tabs would wrap onto a line of its own.
 *
 * The outlet is the exception and lives in the bar with the tabs, where every
 * other module keeps its scope. It is not one of the four: those ask which
 * products, and this asks which shop — and it applies to this tab alone, since
 * categories, choice groups and options have no outlet of their own.
 */
export function ProductsPanel({ branchId, onClearBranch, initialBrandId }: ProductsPanelProps) {
  const [filters, setFilters] = useState<ProductFilterValues>(() => ({
    ...emptyProductFilters,
    brandId: initialBrandId ?? null,
  }))
  const [page, setPage] = useState(0)
  // The outlet is chosen one level up, so a change arrives as a prop rather
  // than through `applyFilters`. Resetting during render keeps it to a single
  // pass — an effect would paint one frame of a page that the shorter list may
  // not have. Same pattern the search box uses in `ProductFilters`.
  const [lastBranchId, setLastBranchId] = useState(branchId)

  if (branchId !== lastBranchId) {
    setLastBranchId(branchId)
    setPage(0)
  }

  const [editingId, setEditingId] = useState<string | null>(null)
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [pendingDelete, setPendingDelete] = useState<ProductSummary | null>(null)
  const [isImportOpen, setIsImportOpen] = useState(false)

  const query = useMemo<ProductListQuery>(
    () => ({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      searchText: filters.searchText.trim() || undefined,
      categoryIds: filters.categoryId ? [filters.categoryId] : undefined,
      brandIds: filters.brandId ? [filters.brandId] : undefined,
      enabled: filters.enabled ?? undefined,
      status: filters.status ?? undefined,
      branchId: branchId ?? undefined,
    }),
    [filters, page, branchId],
  )

  const { products, total, isLoading, isRefreshing, error, retry } = useProducts(query)
  const { categories } = useProductCategories()
  const { brands } = useProductBrands()
  const { remove, toggleEnabled, togglePublished } = useProductActions()
  const { exportCatalogue } = useProductImport()

  // The outlet counts as a filter. Without it an empty branch would read as
  // "No products yet" over a catalogue the merchant can see is not empty, and
  // offer to create one they already have.
  const isFiltered =
    Boolean(filters.searchText.trim()) ||
    Boolean(filters.categoryId) ||
    Boolean(filters.brandId) ||
    Boolean(filters.enabled) ||
    Boolean(filters.status) ||
    Boolean(branchId)

  // A filter narrowing the list can strand the merchant on a page that no longer
  // exists, so changing one always returns to the first.
  function applyFilters(next: ProductFilterValues) {
    setFilters(next)
    setPage(0)
  }

  function openForm(productId: string | null) {
    setEditingId(productId)
    setIsFormOpen(true)
  }

  const busyIds = [
    toggleEnabled.isPending ? toggleEnabled.variables?.id : null,
    togglePublished.isPending ? togglePublished.variables?.id : null,
  ].filter((id): id is string => Boolean(id))

  function handleRowAction(action: ProductRowAction, product: ProductSummary) {
    switch (action) {
      case 'edit':
        return openForm(product.id)
      case 'publish':
      case 'unpublish':
        return void togglePublished.mutate({ id: product.id, published: action === 'publish' })
      case 'enable':
      case 'disable':
        return void toggleEnabled.mutate({ id: product.id, enabled: action === 'enable' })
      case 'delete':
        setPendingDelete(product)
        return
    }
  }

  async function confirmDelete() {
    if (!pendingDelete) return

    await remove.mutateAsync(pendingDelete.id)
    setPendingDelete(null)

    // The row that went may have been the last one on this page.
    if (products.length === 1 && page > 0) setPage((current) => current - 1)
  }

  return (
    <>
      <ModuleIntro
        description="Everything you sell, with its variants, prices and add-ons."
        action={
          <div className="flex flex-wrap items-center gap-2">
            {/* Export carries the filters, not the page — see `exportProducts`.
                Disabled while the list is empty: a workbook of nothing but
                headings is the template, and that is the import dialog's job. */}
            <Button
              variant="ghost"
              size="sm"
              className="border-border h-9 rounded-full border px-4 text-xs"
              isDisabled={exportCatalogue.isPending || total === 0}
              onPress={() => exportCatalogue.mutate(query)}
            >
              <DownloadIcon width={14} height={14} />
              {exportCatalogue.isPending ? 'Exporting…' : 'Export'}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="border-border h-9 rounded-full border px-4 text-xs"
              onPress={() => setIsImportOpen(true)}
            >
              <UploadIcon width={14} height={14} />
              Import
            </Button>

            <Button
              variant="primary"
              size="sm"
              className="h-9 rounded-full px-4 font-medium"
              onPress={() => openForm(null)}
            >
              <PlusIcon width={16} height={16} />
              New product
            </Button>
          </div>
        }
      />

      {exportCatalogue.error != null && (
        <p role="alert" className="text-danger text-xs">
          {errorMessage(exportCatalogue.error, 'Could not export your products. Please try again.')}
        </p>
      )}

      <ProductFilters
        value={filters}
        onChange={applyFilters}
        categories={categories}
        brands={brands}
        isDisabled={isLoading}
      />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        /* A failed list must not read as an empty catalogue — the merchant
           would go looking for products they still have. */
        <ErrorState subject="your products" error={error} onRetry={retry} />
      ) : products.length === 0 ? (
        <EmptyState
          title={isFiltered ? 'Nothing matches those filters.' : 'No products yet.'}
          description={
            isFiltered
              ? 'Try a broader search, or clear the filters to see the whole catalogue.'
              : 'Add the first one and it will show up here, ready to publish.'
          }
          action={
            isFiltered
              ? {
                  label: 'Clear filters',
                  onPress: () => {
                    applyFilters(emptyProductFilters)
                    onClearBranch?.()
                  },
                }
              : { label: 'New product', onPress: () => openForm(null) }
          }
        />
      ) : (
        <>
          {/* Refetching dims the table rather than replacing it, so paging and
              filtering do not throw the merchant back to a skeleton. */}
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <ProductTable products={products} onAction={handleRowAction} busyIds={busyIds} />
          </div>

          <Pagination page={page} pageSize={PAGE_SIZE} total={total} onPageChange={setPage} />
        </>
      )}

      <ProductFormDialog
        // Remounting on the target clears the previous product's values before the
        // next one's arrive — without it the form briefly shows the wrong product.
        key={editingId ?? 'new'}
        isOpen={isFormOpen}
        onOpenChange={setIsFormOpen}
        productId={editingId}
        onSaved={() => setIsFormOpen(false)}
      />

      <ImportProductsDialog isOpen={isImportOpen} onOpenChange={setIsImportOpen} />

      <DeleteProductDialog
        product={pendingDelete}
        onOpenChange={(isOpen) => !isOpen && setPendingDelete(null)}
        onConfirm={confirmDelete}
        isDeleting={remove.isPending}
        error={remove.error}
      />
    </>
  )
}
